import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { DashboardSearchService, DashboardService } from '@app/services/app/dashboard.service';
import { ProjectsService, ProjectsEditorService } from 'app/services/app/projects.service';
import { AppRouterService } from 'app/services/app/router.service';

import * as Objects from '@core/objects';
import * as Search from '@app/models/search';


@Component({
    selector: 'dashboard-views-table-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss']
})
export class DashboardViewsTableMainComponent implements OnInit, OnDestroy {

    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_NOT_ALLOWED = 2;
    public readonly STATUS_NOT_FOUND = 3;
    public readonly STATUS_ERROR = 4;

    public readonly SEARCH_STATUS = Search.SearchHttpStatus;

    protected readonly  availableEvents: any = {
        "request.version.uploaded": "badge_artwork",
        "request.created": "badge_new_request",
        "request.status.updated": "badge_status_update"
    };

    public status: number = 0;
    public sorting: boolean = false;
    public hasSearch: boolean = false;
    public loadingMore: boolean = false;
    public resultsStatus: number = Search.SearchHttpStatus.INITIAL;
    public items: Array<Objects.ExtendedRequest> = [];
    public excludedLabels: Objects.Entity[] = [];
    public openedProject: Objects.ExtendedRequest = null;
    public summaryLoadingId: number = null;
    public order: string = '';
    public orderedField: string = '';

    private transitions: Objects.WorkflowTransition[] = [];

    public events: {name: string, id: number | string}[] = [];
    public updating: Array<number | string> = [];


    protected resultsSubscriptor: Subscription;
    protected resultsStatusSubscriptor: Subscription;
    protected appliedFiltersSubscription: Subscription;
    protected projectSubscriptor: Subscription;
    protected appliedSortSubscription: Subscription;
    protected eventsSubscription: Subscription;
    protected updatingSubscription: Subscription;
    protected termSubscription: Subscription;
    protected errorSubscriptor: Subscription;
    protected stagesSubscriptor: Subscription;

	constructor(
        private dashSrv: DashboardService,
        private searchSrv: DashboardSearchService,
        private appRouter: AppRouterService,
        private projectsSrv: ProjectsService,
        private projectsEditorSrv: ProjectsEditorService,
    ){}

	ngOnInit(){
        this.projectsEditorSrv.loadWorkflowTransitions();

        this.resultsSubscriptor = this.dashSrv.selectResults().subscribe((projects: Objects.ExtendedRequest[]) => {
            this.items = projects;
            this.loadingMore = false; // for stop loading more
            if(projects != null && projects.length > 0){
                this.orderedField = this.order;
                this.sorting = false;
            }
        });

        this.stagesSubscriptor = this.projectsEditorSrv.selectAllWorkflowTransitions()
        .subscribe((transitions: Objects.WorkflowTransition[]) => {
            if(transitions != null){
                this.transitions = transitions;
            }
        });

        this.resultsStatusSubscriptor = this.dashSrv.selectResultStatus().subscribe((rStatus: number) => {
            this.resultsStatus = rStatus;
        });

        this.appliedFiltersSubscription = this.searchSrv.selectFilters().subscribe((filters: Search.SearchAppliedFilter[]) => {
            this.hasSearch = (filters.length >0) ? true : false;
            this.excludedLabels = filters.filter((item) => item.key == 'label').map((f)=> f.item);
        });

        this.projectSubscriptor = this.projectsSrv.selectProject().subscribe((project: Objects.ExtendedRequest ) => {
            if(project != null){
                this.openedProject = project;
                this.summaryLoadingId = null;
            }
        });

        this.appliedSortSubscription = this.dashSrv.selectOrder().subscribe((order: string) => {
            // this.sorting = true;
            this.order = order;
            this.orderedField = this.order;
            this.sorting = false;
        });

        this.eventsSubscription = this.dashSrv.selectEvents().subscribe((events: {name: string, id: number | string}[]) => this.filterEvents(events));
        this.updatingSubscription = this.dashSrv.selectUpdating().subscribe((updating: Array<number | string>) => this.updating = updating);
        this.termSubscription = this.searchSrv.selectTerm().subscribe((term: string) => this.hasSearch = (term && term !='') );

        this.errorSubscriptor = this.dashSrv.selectError('load_results').subscribe((error: HttpErrorResponse) => this.setError(error));

    }

    ngOnDestroy() {
        this.resultsSubscriptor.unsubscribe();
        this.appliedFiltersSubscription.unsubscribe();
        this.projectSubscriptor.unsubscribe();
        this.appliedSortSubscription.unsubscribe();
        this.eventsSubscription.unsubscribe();
        this.updatingSubscription.unsubscribe();
        this.termSubscription.unsubscribe();
        this.errorSubscriptor.unsubscribe();
        this.stagesSubscriptor.unsubscribe();
    }

    loadMore() {
        this.loadingMore = true;
        this.dashSrv.loadResults();
    }

    onOpen(project: Objects.ExtendedRequest){
        this.projectsSrv.clear();
        this.appRouter.navigate('/projects/'+project.id);
    }

    onSummaryToggle(project: Objects.ExtendedRequest){

        if(this.openedProject != null){
            let previous = this.openedProject.id;
            this.openedProject = null;
            this.projectsSrv.clear();
            if( previous != project.id ){
                this.conditionalLoad(project);
            } else {
                this.summaryLoadingId = null;
            }
            return;
        }
        // in first open
        this.conditionalLoad(project)
    }

    isSummaryOpen(project: Objects.ExtendedRequest){
        if(this.openedProject == null)
            return false;

        return this.openedProject.id == project.id;
    }

    onLabelClick(tag: Objects.Label):void{
        if (tag != null ){
            // send this tag to the filters here!!!
            this.searchSrv.applyFilter({key:"label",item: tag});
            
        }
    }

    onStageClick(project: Objects.ExtendedRequest) {
        this.dashSrv.updateProjectStatus(project.id, project.status);
    }

    onScroll(){
        // load more only if there is no final.
        if(this.resultsStatus ==  Search.SearchHttpStatus.DONE ){
            this.loadMore();
        }
    }

    private conditionalLoad(project: Objects.ExtendedRequest){
        if(project.target_version == null){
            this.summaryLoadingId = project.id;
            this.projectsSrv.load(project.id);
        } else {
            // 12/12/2022 in this case we load project in it's store with only uploaded versions
            this.projectsSrv.loadWithUploaded(project.id);
            this.openedProject = project;
            this.summaryLoadingId = null;
        }
    }

    private filterEvents(events: {name: string, id: number | string}[]) {
        this.events = events.filter((event: {name: string, id: number | string}) => Object.keys(this.availableEvents).indexOf(event.name)>-1);
    }

    public eventBadge(id: number | string) : string {
        let events = this.events.filter((event: {name: string, id: number | string}) => event.id == id);

        if(events.length == 0)
            return "";

        // fix problem with status_update and create
        let eventName = events.pop().name;
        if(events.length > 0)
        {
            let leventName = events.pop().name;

            eventName = leventName=="request.created" && eventName == "request.status.updated"? leventName : eventName;
        }

        return this.availableEvents[eventName];
    }

    public isProjectUpdating(id: number | string) : boolean {
        return this.updating.some((uid: number | string) => uid == id);
    }

    protected setError(error: HttpErrorResponse) {
        if(error == null){
            this.status = this.STATUS_DONE; // reset front
            return;
        }

        switch(error.status) {
            case 500:
                this.status = this.STATUS_ERROR;
                break;
            case 404:
                this.status = this.STATUS_NOT_FOUND;
                break;
            case 403:
                this.status = this.STATUS_NOT_ALLOWED;
                break;
        }
    }

}
